import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  DsmAddendumEnvelope,
  DsmAddendumSelectionValidation,
  DsmAddendumSignOffStatus,
  DsmAffectedAddendums,
  DsmSendAddendumSigningRequest,
  DsmSigningSession,
  DsmUploadedDocumentVerification
} from '../models/dsm-addendum-sign-off.models';

@Injectable({ providedIn: 'root' })
export class DsmAddendumSignOffService {
  readonly #http = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);
  readonly #baseUrl = this.#apiConfig.baseUrlV2;

  validateSelection(dealId: string, addendumIds: string[]): Observable<DsmAddendumSelectionValidation> {
    return this.#http.postData<DsmAddendumSelectionValidation>(
      `${this.#baseUrl}/addendum-documents/deal/${dealId}/validate-selection`,
      { showLoader: false, showMessage: false, showError: false },
      { addendumIds }
    );
  }

  verifyUploadedDocument(
    dealId: string,
    s3DocumentId: string,
    addendumIds: string[],
    acknowledgeMissingSigners = false
  ): Observable<DsmUploadedDocumentVerification> {
    return this.#http.postData<DsmUploadedDocumentVerification>(
      `${this.#baseUrl}/addendum-documents/deal/${dealId}/upload/verify`,
      { showLoader: false, showMessage: false, showError: false },
      { s3DocumentId, addendumIds, acknowledgeMissingSigners }
    );
  }

  send(dealId: string, request: DsmSendAddendumSigningRequest): Observable<unknown> {
    return this.#http.postData<unknown>(
      `${this.#baseUrl}/addendum-documents/deal/${dealId}/send`,
      // showError off: the sign-off modal shows the one error toast (and the inline message) itself.
      { showLoader: false, showMessage: true, showError: false },
      request
    );
  }

  getEnvelopes(dealId: string): Observable<DsmAddendumEnvelope[]> {
    return this.#http.getData<DsmAddendumEnvelope[]>(`${this.#baseUrl}/addendum-documents/deal/${dealId}/envelopes`, {
      showLoader: false,
      showMessage: false
    });
  }

  getStatusByDeal(dealId: string): Observable<DsmAddendumSignOffStatus[]> {
    return this.#http.getData<DsmAddendumSignOffStatus[]>(`${this.#baseUrl}/addendum-documents/deal/${dealId}/status`, {
      showLoader: false,
      showMessage: false
    });
  }

  sendToBuilder(dealDocumentId: string): Observable<void> {
    return this.#http.postData<void>(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/send-to-builder`,
      { showLoader: false, showMessage: true },
      {}
    );
  }

  getBuilderSignature(dealDocumentId: string): Observable<string> {
    return this.#http.postData<string>(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/get-builder-signature`,
      { showLoader: false, showMessage: true },
      {}
    );
  }

  resend(dealDocumentId: string): Observable<void> {
    return this.#http.postData<void>(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/resend`,
      { showLoader: false, showMessage: true },
      {}
    );
  }

  getVoidPreview(dealDocumentId: string): Observable<DsmAffectedAddendums> {
    return this.#http.getData<DsmAffectedAddendums>(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/void-preview`,
      { showLoader: false, showMessage: false }
    );
  }

  void(dealDocumentId: string, voidReason: string): Observable<void> {
    return this.#http.postData<void>(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/void`,
      { showLoader: false, showMessage: true },
      { voidReason }
    );
  }

  downloadSigned(dealDocumentId: string, downloadFileName?: string): Observable<void> {
    return this.#http.downloadReportFile(
      `${this.#baseUrl}/addendum-documents/${dealDocumentId}/download-signed`,
      { showLoader: false, showMessage: false },
      undefined,
      downloadFileName
    );
  }

  startInPersonSigning(
    dealDocumentId: string,
    signerId: string,
    signerName: string,
    returnUrl?: string
  ): Observable<DsmSigningSession> {
    const payload: Record<string, string> = { signerId, signerName };
    if (returnUrl) payload['returnUrl'] = returnUrl;

    return this.#http.postData<DsmSigningSession>(
      `${this.#baseUrl}/deal-documents/${dealDocumentId}/docusign/in-person/start`,
      { showLoader: false, showMessage: false },
      payload
    );
  }
}
